import isEqual from 'lodash/isEqual';
import { formatPrice } from '@spotahome/soyuz-currency';
import { trans } from '@spotahome/soyuz/client';

import {
  DEFAULT_FILTERS,
  DEFAULT_FILTERS_TRANSLATED_NO_SORT,
  EXCLUDED_FILTERS_FROM_COUNT
} from '../constants';

import {
  filtersObjectFromUrl,
  shouldAvoidParamInTheUrl as paramHasDefaultValue
} from './search-url-generator';

import { getSortingName, isSortByPOI, getDefaultSortBy } from './sorting';

export const getActiveFilters = filters =>
  Object.entries(filters).filter(
    ([key, value]) =>
      !!value &&
      !EXCLUDED_FILTERS_FROM_COUNT.includes(key) &&
      !paramHasDefaultValue(key, value) &&
      (!Array.isArray(value) || value.length > 0)
  );
export const getActiveFiltersCount = filters =>
  Object.entries(filters).filter(
    ([key, value]) =>
      !!value &&
      !EXCLUDED_FILTERS_FROM_COUNT.includes(key) &&
      !paramHasDefaultValue(key, value) &&
      (!Array.isArray(value) || value.length > 0)
  ).length;

export const getBudgetValue = ({ minBudget, maxBudget, maxPrice }) => {
  const maxBudgetConditional = maxBudget ? `-${maxBudget}` : '';
  let budgetValue = `${minBudget}${maxBudgetConditional}`;

  if (maxPrice && maxBudget >= maxPrice) {
    budgetValue = minBudget ? `${minBudget}` : '';
  }
  return budgetValue;
};

const adaptCommonFilters = (filters, sortingAlgorithm) => {
  let sortBy = getSortingName(filters.sortBy, sortingAlgorithm);

  if (isSortByPOI(sortBy) && (!filters.poiLat || !filters.poiLng)) {
    sortBy = getDefaultSortBy();
  }

  return {
    bed: filters.bed,
    budget: filters.budget,
    contractDurationTypesAllowed: filters.contractDurationTypesAllowed,
    page: filters.page,
    noDeposit: filters.noDeposit,
    instantBooking: filters.instantBooking,
    plan: filters.plan,
    poiLat: filters.poiLat ? parseFloat(filters.poiLat) : null,
    poiLng: filters.poiLng ? parseFloat(filters.poiLng) : null,
    verified: filters.verified,
    isBestChoice: filters.isBestChoice,
    sortBy
  };
};

export const adaptFiltersFromUrl = (filters, sortingAlgorithm) => ({
  ...adaptCommonFilters(filters, sortingAlgorithm),
  flexibleMoveIn: parseInt(filters.flexibleMoveIn, 10),
  flexibleMoveOut: parseInt(filters.flexibleMoveOut, 10),
  bathrooms: filters['bathrooms[]'],
  features: filters['features[]'],
  moveIn: filters['move-in'],
  moveOut: filters['move-out'],
  rentalType: filters['rentalType[]'],
  topology: filters['topology[]'],
  type: filters['type[]'],
  ids: filters['ids[]'],
  areaId: filters['areaId[]'],
  moveOutFrom: filters.moveOutFrom,
  moveOutTo: filters.moveOutTo,
  moveInFrom: filters.moveInFrom,
  moveInTo: filters.moveInTo
});

export const adaptFiltersToUrl = (filters, sortingAlgorithm) => ({
  ...adaptCommonFilters(filters, sortingAlgorithm),
  flexibleMoveIn: filters.flexibleMoveIn,
  flexibleMoveOut: filters.flexibleMoveOut,
  'bathrooms[]': filters.bathrooms,
  'features[]': filters.features,
  'move-in': filters.moveIn,
  'move-out': filters.moveOut,
  'rentalType[]': filters.rentalType,
  'topology[]': filters.topology,
  'type[]': filters.type,
  'ids[]': filters.ids,
  'areaId[]': filters.areaId,
  moveOutFrom: filters.moveOutFrom,
  moveOutTo: filters.moveOutTo,
  moveInFrom: filters.moveInFrom,
  moveInTo: filters.moveInTo
});

export const adaptSortByToUrl = (filters, sortingAlgorithm) => {
  const sortBy = getSortingName(filters.sortBy, sortingAlgorithm);

  return { ...filters, sortBy };
};

export const getInitialFilters = (originalUrl, areaInfo) => ({
  ...DEFAULT_FILTERS,
  ...filtersObjectFromUrl(originalUrl, areaInfo)
});

export const getDefaultFilters = () => DEFAULT_FILTERS;

export const isDefaultFilter = filters => {
  const { sortBy, ...restFilters } = filters;

  return Object.entries(restFilters).every(([key, value]) =>
    isEqual(DEFAULT_FILTERS_TRANSLATED_NO_SORT[key], value)
  );
};

export const formatBudget = (budget, currencyIsoCode) => {
  const [minPrice, maxPrice] = budget.split('-');
  const minPriceFormatted = formatPrice(minPrice, currencyIsoCode);
  if (maxPrice) {
    return `${minPriceFormatted} - ${formatPrice(maxPrice, currencyIsoCode)}`;
  }
  return `${trans('filters.price.from')} ${minPriceFormatted}`;
};

export const groupFilterCategory = category => {
  switch (category) {
    case 'rentalType[]':
      return {
        id: 'availabilities',
        title: trans('search.filters.remove.availabilities'),
        appearanceOrder: 1,
        defaultValues: {
          'rentalType[]': []
        }
      };
    case 'move-in':
    case 'move-out':
    case 'moveInFrom':
    case 'moveInTo':
      return {
        id: 'dates',
        title: trans('search.filters.remove.dates'),
        appearanceOrder: 2,
        defaultValues: {
          'move-in': '',
          'move-out': '',
          moveInFrom: '',
          moveInTo: ''
        }
      };
    case 'budget':
      return {
        id: 'budget',
        title: trans('search.filters.remove.budget'),
        appearanceOrder: 3,
        defaultValues: {
          budget: ''
        }
      };
    case 'type[]':
    case 'topology[]':
      return {
        id: 'propertyType',
        title: trans('search.filters.remove.property-type'),
        appearanceOrder: 4,
        defaultValues: {
          'topology[]': [],
          'type[]': []
        }
      };
    case 'features[]':
      return {
        id: 'features',
        title: trans('search.filters.remove.features'),
        appearanceOrder: 5,
        defaultValues: {
          'features[]': []
        }
      };
    case 'bathrooms[]':
    case 'bed':
    case 'areaId[]':
    case 'noDeposit':
    case 'instantBooking':
    case 'verified':
    case 'isBestChoice':
      return {
        id: 'advanced-filters',
        title: trans('search.filters.remove.advanced-filters'),
        appearanceOrder: 6,
        defaultValues: {
          noDeposit: 0,
          instantBooking: 0,
          verified: 0,
          isBestChoice: 0,
          'bathrooms[]': [],
          bed: '',
          'areaId[]': []
        }
      };
    default:
      return {
        id: 'default',
        title: trans('search.prefilters.summary.title'),
        appearanceOrder: 1,
        defaultValues: getDefaultFilters()
      };
  }
};

export default {
  adaptFiltersFromUrl,
  adaptFiltersToUrl,
  adaptSortByToUrl,
  getActiveFiltersCount,
  getActiveFilters,
  getInitialFilters,
  getDefaultFilters,
  isDefaultFilter,
  formatBudget,
  groupFilterCategory,
  getBudgetValue
};
